// My Trading Journey → My edge & milestones (/app/journey/edge-milestones): what works over a
// whole career, and the story it made. Every setup with each year's result (an edge or a leak
// only after CAREER_TRADES trades), the Behavior patterns that cost money, the chapters of the
// journey with the milestones earned in each, and the next milestones. Insights is about now;
// this is about the long run. The numbers come from journey.js.
import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useJourney } from './JourneyStory';
import { AddMilestone } from './JourneyPages';
import { CAREER_TRADES, CHAPTER_NAMES, JOURNEY_MILESTONE_TYPES, MILESTONE_TEXT, MILESTONE_TYPES, careerEdge, chapters, journeyOverview, milestoneList, milestones } from './journey';
import { formatMoney, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const whole = (n) => usd.format(Math.round(Math.abs(n))).replace(/\.00$/, '');
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${whole(n)}`;
const monthName = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });
const dayText = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
const VERDICTS = { edge: 'Edge', leak: 'Leak', unproven: 'Not proven yet' };

function milestoneTitle(m, t) {
  if (MILESTONE_TEXT[m.id]) return t(MILESTONE_TEXT[m.id][1]);
  if (m.title || m.name) return m.title || m.name;
  const type = MILESTONE_TYPES[m.type];
  if (!type) return m.id;
  if (m.type === 'clean_month') return t(type.label);
  return `${t(type.label)}: ${type.unit === '$' ? whole(m.target) : m.target.toLocaleString(currentLocale())}`;
}
const iconOf = (m) => (MILESTONE_TEXT[m.id] ? MILESTONE_TEXT[m.id][0] : '🏁');

// Every setup over the whole career: the long-run verdict and each year's result.
function Setups({ r }) {
  const { t } = useI18n();
  const edges = r.setups.filter((s) => s.verdict === 'edge');
  const leaks = r.setups.filter((s) => s.verdict === 'leak');
  if (!r.hasSetups) {
    return (
      <section className="jo-card">
        <h2>{t('Your edge')}</h2>
        <p className="empty">{t('Tag your trades with a setup, or add plays in your Playbook, and your edge over the long run shows here.')}</p>
      </section>
    );
  }
  return (
    <section className="jo-card">
      <h2>{t('Your edge')}</h2>
      <p className="jg-lead">
        {edges.length ? t(edges.length === 1 ? 'One setup has made money over the long run: {names}.' : '{n} setups have made money over the long run: {names}.', { n: edges.length, names: edges.map((s) => s.label).join(', ') })
          : t('No setup has proven itself over the long run yet.')}
        {' '}{leaks.length > 0 && t(leaks.length === 1 ? '{names} has cost you money.' : '{names} have cost you money.', { names: leaks.map((s) => s.label).join(', ') })}
      </p>
      <div className="table-wrap">
        <table className="biz-table jg-table">
          <thead>
            <tr>
              <th>{t('Setup')}</th><th className="num">{t('Trades')}</th><th className="num">{t('Win rate')}</th><th className="num">{t('Per trade')}</th><th className="num">{t('Total')}</th>
              {r.years.map((y) => <th key={y} className="num hide-sm">{y}</th>)}
              <th>{t('Over the long run')}</th>
            </tr>
          </thead>
          <tbody>
            {r.setups.map((s) => (
              <tr key={s.id}>
                <th scope="row">{s.label}</th>
                <td className="num">{s.count}</td>
                <td className="num">{Math.round(s.winRate)}%</td>
                <td className={`num ${tone(s.expectancy)}`}>{formatMoney(s.expectancy)}</td>
                <td className={`num ${tone(s.net)}`}>{signed(s.net)}</td>
                {r.years.map((y) => {
                  const x = s.yearly.find((z) => z.year === y);
                  return <td key={y} className={`num hide-sm ${x ? tone(x.net) : ''}`} title={x ? t(x.count === 1 ? '{n} trade' : '{n} trades', { n: x.count }) : undefined}>{x ? signed(x.net) : '—'}</td>;
                })}
                <td><span className={`jg-verdict ${s.verdict}`}>{t(VERDICTS[s.verdict])}</span>{s.yearly.length > 1 && <small className="jg-years"> {t('green {n} of {total} years', { n: s.greenYears, total: s.yearly.length })}</small>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="field-hint">
        {t('A setup is an edge after {n} trades, earning per trade and green in at least half the years you traded it. A leak loses per trade over {n} trades or more. Insights shows the same setups for this month.', { n: CAREER_TRADES })}
        {r.untagged > 0 && ` ${t('{n} trades have no setup tag, so they aren’t in this table.', { n: r.untagged })}`}
      </p>
    </section>
  );
}

// The Behavior patterns that have cost money over the whole career.
function Leaks({ r }) {
  const { t } = useI18n();
  if (!r.leaks.length) return null;
  const max = Math.max(...r.leaks.map((p) => -p.net), 1);
  return (
    <section className="jo-card">
      <h2>{t('What costs you money')}</h2>
      <p className="field-hint">{t('Patterns Behavior found in your trades since you started, and what the flagged trades lost together.')}</p>
      <ul className="jo-cats jg-leaks">
        {r.leaks.map((p) => (
          <li key={p.kind}>
            <span>{t(p.name)} <small className="jg-years">{t(p.count === 1 ? '{n} trade' : '{n} trades', { n: p.count })}</small></span>
            <span className="jo-cat-bar"><b style={{ width: `${(-p.net / max) * 100}%`, background: 'var(--loss)' }} /></span>
            <strong className="loss">{signed(p.net)}</strong>
          </li>
        ))}
      </ul>
    </section>
  );
}

// The journey in chapters, newest first, with the milestones earned in each.
function Story({ o, list, story, saveStory }) {
  const { t } = useI18n();
  const parts = useMemo(() => (o ? chapters(o) : []), [o]);
  const [renaming, setRenaming] = useState(null);
  if (!parts.length) return null;
  const nameOf = (c) => {
    const saved = ((story && story.chapters) || []).find((x) => x.id === c.id);
    return saved && saved.title ? saved.title : t(CHAPTER_NAMES[c.id]);
  };
  async function rename(e) {
    e.preventDefault();
    const title = renaming.title.trim() === t(CHAPTER_NAMES[renaming.id]) ? '' : renaming.title.trim();
    const text = renaming.text.trim();
    await saveStory({ ...story, chapters: [...story.chapters.filter((c) => c.id !== renaming.id), ...(title || text ? [{ id: renaming.id, title, text }] : [])] });
    setRenaming(null);
  }
  const textOf = (c) => {
    const saved = ((story && story.chapters) || []).find((x) => x.id === c.id);
    return saved && saved.text;
  };
  return (
    <section className="jo-card">
      <h2>{t('Your story')}</h2>
      <p className="field-hint">{t('Your journey in chapters, with the milestones you earned along the way. Real money and simulated performance are kept apart.')}</p>
      <ol className="jg-chapters">
        {[...parts].reverse().map((c) => {
          const earned = list.filter((m) => m.earnedOn && m.earnedOn.slice(0, 7) >= c.from && m.earnedOn.slice(0, 7) <= c.to).sort((a, b) => a.earnedOn.localeCompare(b.earnedOn));
          return (
            <li key={c.id} className="jg-chapter">
              <p className="js-ch-num">{t('Chapter {n}', { n: c.number })} · {monthName(c.from)}{c.to !== c.from ? ` – ${monthName(c.to)}` : ''}</p>
              {renaming && renaming.id === c.id ? (
                <form className="jg-rename" onSubmit={rename}>
                  <input value={renaming.title} maxLength={60} onChange={(e) => setRenaming({ ...renaming, title: e.target.value })} aria-label={t('Chapter name')} />
                  <textarea value={renaming.text} maxLength={280} rows={2} onChange={(e) => setRenaming({ ...renaming, text: e.target.value })} placeholder={t('A line about this chapter (optional)')} aria-label={t('A line about this chapter (optional)')} />
                  <span className="js-note-actions">
                    <button type="submit" className="small">{t('Save')}</button>
                    <button type="button" className="secondary small" onClick={() => setRenaming(null)}>{t('Cancel')}</button>
                  </span>
                </form>
              ) : (
                <>
                  <div className="js-ch-title">
                    <h3>{nameOf(c)}</h3>
                    {saveStory && <button type="button" className="js-note-btn" onClick={() => setRenaming({ id: c.id, title: nameOf(c), text: textOf(c) || '' })}>{t('Rename')}</button>}
                  </div>
                  {textOf(c) && <p className="js-ch-text">{textOf(c)}</p>}
                </>
              )}
              <ul className="jg-ch-stats">
                <li><span>{t('Real money')}</span><strong className={tone(c.cash)}>{signed(c.cash)}</strong></li>
                {c.hasProp && <li><span>{t('Performance (simulated)')}</span><strong className={tone(c.prop)}>{signed(c.prop)}</strong></li>}
                <li><span>{t('Accounts blown')}</span><strong>{c.blown}</strong></li>
                <li><span>{t('Passed')}</span><strong>{c.passed}</strong></li>
                <li><span>{t('Payouts')}</span><strong>{c.payouts}</strong></li>
              </ul>
              {earned.length > 0 && (
                <ul className="jg-earned">
                  {earned.map((m) => <li key={m.id}><span aria-hidden="true">{iconOf(m)}</span> {milestoneTitle(m, t)} <small>{dayText(m.earnedOn)}</small></li>)}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// The journey milestones still ahead, closest first.
function NextMilestones({ list, settings, onSave }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const ahead = list.filter((m) => !m.earnedOn).sort((a, b) => b.have / b.need - a.have / a.need).slice(0, 4);
  const earned = list.filter((m) => m.earnedOn).length;
  function remove(m) {
    if (m.custom) onSave({ ...settings, custom: (settings.custom || []).filter((x) => x.id !== m.id) });
    else if (m.added) onSave({ ...settings, added: (settings.added || []).filter((x) => x !== m.id) });
    else onSave({ ...settings, hidden: [...(settings.hidden || []), m.id] });
  }
  return (
    <section className="jo-card">
      <div className="jo-card-head">
        <h2>{t('Milestones')}</h2>
        {onSave && (
          <span className="jm-head-actions">
            <button type="button" className="secondary small" onClick={() => setEditing((v) => !v)}>{editing ? t('Done') : t('Edit')}</button>
            <button type="button" className="small" onClick={() => setAdding(true)}>+ {t('Add')}</button>
          </span>
        )}
      </div>
      <p className="field-hint">{t('{n} of {total} journey milestones earned: passes, payouts, accounts that lasted, going live.', { n: earned, total: list.length })}</p>
      {adding && <AddMilestone settings={settings} onSave={(next) => { onSave(next); setAdding(false); }} onClose={() => setAdding(false)} />}
      {editing && (
        <ul className="jg-edit-list">
          {list.map((m) => (
            <li key={m.id}>
              <span><span aria-hidden="true">{iconOf(m)}</span> {milestoneTitle(m, t)}{m.earnedOn && <small> · {dayText(m.earnedOn)}</small>}</span>
              <button type="button" className="link-button biz-delete" onClick={() => remove(m)}>{t(m.custom || m.added ? 'Remove' : 'Hide')}</button>
            </li>
          ))}
        </ul>
      )}
      {ahead.length > 0 && (
        <ul className="jg-next">
          {ahead.map((m) => (
            <li key={m.id}>
              <span className="jg-next-name"><span aria-hidden="true">{iconOf(m)}</span> {milestoneTitle(m, t)}</span>
              <span className="jo-progress-track"><span style={{ width: `${Math.min(100, (m.have / m.need) * 100)}%` }} /></span>
              <small>{m.money ? `${whole(m.have)} / ${whole(m.need)}` : `${m.have.toLocaleString(currentLocale())} / ${m.need.toLocaleString(currentLocale())}`}</small>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function JourneyGrowth({ accounts, trades, notes = {}, library = [], plays = [], user, onUserChange }) {
  const { t } = useI18n();
  const { story, saveStory, scope, scopeExpenses, allAccounts } = useJourney();
  const [allExpenses, setAllExpenses] = useState([]);
  useEffect(() => { api('/expenses').then(setAllExpenses).catch(() => {}); }, []);
  const expenses = useMemo(() => scopeExpenses(allExpenses), [allExpenses, scope, allAccounts]); // eslint-disable-line react-hooks/exhaustive-deps
  const r = useMemo(() => careerEdge(trades, { library, plays, accounts, notes }), [trades, library, plays, accounts, notes]);
  const settings = user && user.journeyMilestones;
  // Journey milestones, plus any the trader added or made themselves.
  const list = useMemo(() => milestones(accounts, trades, notes, undefined, milestoneList(settings || {}).filter((m) => m.custom || m.added || JOURNEY_MILESTONE_TYPES.includes(m.type))), [accounts, trades, notes, settings]);
  async function saveMilestones(next) {
    if (!onUserChange || !user) return;
    onUserChange({ ...user, journeyMilestones: next });
    try {
      const saved = await api('/journey/milestones', { method: 'PUT', body: next });
      onUserChange({ ...user, journeyMilestones: saved });
    } catch (err) {
      window.alert(err.message);
    }
  }
  const o = useMemo(() => journeyOverview(accounts, trades, expenses), [accounts, trades, expenses]);
  if (!trades.length) return <p className="empty">{t('Your edge and your story start with your first trade.')}</p>;
  return (
    <div className="jo jg">
      <Setups r={r} />
      <Leaks r={r} />
      <div className="jo-mid jg-mid">
        <Story o={o} list={list} story={story} saveStory={saveStory} />
        <NextMilestones list={list} settings={settings || {}} onSave={onUserChange && user && !user.isDemo ? saveMilestones : null} />
      </div>
    </div>
  );
}
